import { createFileRoute } from '@tanstack/react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faDesktop,
  faBolt,
  faMobileScreen,
  faVideo,
  faWindowMaximize,
  faSync,
  faLock,
  faGear,
  faStar,
  faDownload,
} from '@fortawesome/pro-solid-svg-icons'
import { VersionCard } from '@/components/capture-app/VersionCard'
import { SectionHeading } from '@/components/capture-app/SectionHeading'
import heroImage from '@/assets/introducing-slide-capture.png'

export const Route = createFileRoute('/capture-app')({
  component: CaptureAppPage,
})

// Direct download URL — update when the build is published
const DIRECT_DOWNLOAD_URL = 'https://semipublic-artifacts.unwritten.media/sparkle-sermon-note-capture/slide-capture-1-2-0.zip';

const APP_STORE_FEATURES = [
  { text: 'One-click updates via Mac App Store', available: true },
  { text: 'Apple-verified, sandboxed security', available: true },
  { text: 'Screen capture (window & full-screen)', available: true },
  { text: 'High-performance Syphon video input', available: false },
]

const DIRECT_FEATURES = [
  { text: 'All capture modes included', available: true },
  { text: 'High-performance Syphon video input', available: true },
  { text: 'Screen capture (window & full-screen)', available: true },
  { text: 'Manual updates required', available: true },
];


function CaptureAppPage() {
  return (
    <div className="min-h-screen flex flex-col font-sans bg-stone-50 dark:bg-mist-950 text-stone-900 dark:text-white">
      <main className="flex-grow">

        {/* ── Hero ─────────────────────────────────────────────────────── */}
        <section className="px-6 pt-16 pb-10 max-w-4xl mx-auto text-center">
          <span className="inline-block mb-4 text-sm font-semibold tracking-widest text-purple-600 uppercase">
            Mac App
          </span>
          <h1 className="text-5xl md:text-6xl font-extrabold tracking-tight mb-5">
            Introducing{' '}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-orange-500">
              Slide Capture
            </span>
          </h1>
          <p className="text-xl text-stone-600 dark:text-stone-400 max-w-2xl mx-auto leading-relaxed mb-8">
            The Mac companion app for Sermon Slides. Capture your presentation output
            in real-time and broadcast every slide directly to your audience's devices.
          </p>

          {/* Quick CTA row */}
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button
              disabled
              className="flex items-center justify-center gap-2 bg-stone-800 dark:bg-mist-700 text-white px-7 py-3.5 rounded-xl font-bold text-base opacity-60 cursor-not-allowed"
              title="Coming soon"
            >
              <AppleIcon />
              Mac App Store
              <span className="ml-1 text-xs font-normal bg-white/20 px-2 py-0.5 rounded-full">
                Coming Soon
              </span>
            </button>
            <a
              href={DIRECT_DOWNLOAD_URL}
              className="flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-7 py-3.5 rounded-xl font-bold text-base transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 transform"
            >
              <DownloadIcon />
              Download for Mac
            </a>
          </div>
        </section>

        {/* ── App Screenshot ───────────────────────────────────────────── */}
        <section className="px-6 pb-16 max-w-5xl mx-auto">
          {/* <div className="rounded-2xl overflow-hidden shadow-2xl border border-stone-200 dark:border-mist-800"> */}
            <img
              src={heroImage}
              alt="Slide Capture Mac app screenshot"
              className="w-full object-cover"
            />
          {/* </div> */}
        </section>

        {/* ── What it does ─────────────────────────────────────────────── */}
        <section className="bg-gradient-to-br from-purple-50 to-orange-50 dark:from-purple-950/30 dark:to-orange-950/30 border-y border-stone-200 dark:border-mist-800 py-20 px-6">
          <div className="max-w-3xl mx-auto text-center">
            <SectionHeading
              title="How it works"
              subtitle="Slide Capture runs silently in the background on your Mac, watches for slide changes, and instantly syncs each new slide to your Sermon Slides room — so your audience always sees exactly what's on screen."
            />
            <div className="grid sm:grid-cols-3 gap-6 mt-2">
              {HOW_IT_WORKS.map((step) => (
                <div key={step.title} className="bg-white dark:bg-mist-900 rounded-2xl border border-stone-200 dark:border-mist-800 shadow-sm p-6 text-left">
                  <div className="text-2xl mb-3">{step.icon}</div>
                  <h3 className="font-bold text-stone-900 dark:text-white mb-1">{step.title}</h3>
                  <p className="text-sm text-stone-500 dark:text-mist-400 leading-relaxed">{step.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Which version? ───────────────────────────────────────────── */}
        <section className="py-20 px-6">
          <div className="max-w-4xl mx-auto">
            <SectionHeading
              title="Which version is right for you?"
              subtitle="Both versions are full-featured for most setups. Choose based on how you capture your presentation output."
            />
            <div className="grid md:grid-cols-2 gap-6">
              <VersionCard
                title="Mac App Store"
                badge="Coming Soon"
                badgeColor="bg-stone-100 dark:bg-mist-800 text-stone-500 dark:text-mist-400"
                description="The safest, most convenient option. Download once from Apple's App Store and get automatic updates whenever a new version ships."
                features={APP_STORE_FEATURES}
                ctaLabel="Mac App Store — Coming Soon"
                ctaDisabled
                footnote="We'll announce when it's available."
              />
              <VersionCard
                title="Direct Download"
                badge="All Features"
                badgeColor="bg-orange-100 text-orange-700"
                description="Includes everything in the App Store version, plus Syphon support for ultra-low-CPU video capture from ProPresenter, Resolume, and other Syphon sources."
                features={DIRECT_FEATURES}
                ctaLabel="Download for Mac"
                ctaHref={DIRECT_DOWNLOAD_URL}
                ctaStyle="secondary"
                footnote="macOS 14.2 Sonoma or later required."
              />
            </div>
          </div>
        </section>

        {/* ── Feature highlights ───────────────────────────────────────── */}
        <section className="bg-white dark:bg-mist-900 border-t border-stone-200 dark:border-mist-800 py-20 px-6">
          <div className="max-w-3xl mx-auto">
            <SectionHeading
              title="Everything you need to broadcast slides"
            />
            <div className="grid sm:grid-cols-2 gap-8">
              {FEATURES.map((f) => (
                <div key={f.title} className="flex gap-4 items-start p-5 rounded-2xl ">
                  <span className="text-2xl shrink-0">{f.icon}</span>
                  <div>
                    <h3 className="font-semibold text-stone-900 dark:text-white mb-0.5">{f.title}</h3>
                    <p className="text-sm text-stone-500 dark:text-mist-400 leading-relaxed">{f.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Bottom CTA ───────────────────────────────────────────────── */}
        <section className="py-20 px-6 text-center">
          <div className="max-w-xl mx-auto">
            <h2 className="text-3xl font-bold mb-4 text-stone-900 dark:text-white">Ready to get started?</h2>
            <p className="text-stone-500 dark:text-mist-400 mb-8 leading-relaxed">
              Download Slide Capture and pair it with your free Sermon Slides account.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href={DIRECT_DOWNLOAD_URL}
                className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5 flex items-center justify-center gap-2"
              >
                <DownloadIcon />
                Download for Mac
              </a>
            </div>
          </div>
        </section>

      </main>
    </div>
  )
}

// ── Static data ────────────────────────────────────────────────────────────────

const HOW_IT_WORKS = [
  {
    icon: <FontAwesomeIcon icon={faDesktop} className="text-purple-600" />,
    title: 'Capture',
    body: 'Select your presentation window, virtual camera, or Syphon source in one click.',
  },
  {
    icon: <FontAwesomeIcon icon={faBolt} className="text-purple-600" />,
    title: 'Detect',
    body: 'Slide Capture automatically detects when a new slide appears so nothing is missed.',
  },
  {
    icon: <FontAwesomeIcon icon={faMobileScreen} className="text-purple-600" />,
    title: 'Broadcast',
    body: 'Each slide is instantly synced to your Sermon Slides room for your audience.',
  },
]

const FEATURES = [
  {
    icon: <FontAwesomeIcon icon={faVideo} className="text-orange-500" />,
    title: 'Syphon video input',
    body: 'Low-CPU capture from ProPresenter, Resolume, and other Syphon-enabled apps. Direct download only.',
  },
  {
    icon: <FontAwesomeIcon icon={faWindowMaximize} className="text-orange-500" />,
    title: 'Screen & window capture',
    body: 'Capture any window or the full screen — no special software integration required.',
  },
  {
    icon: <FontAwesomeIcon icon={faSync} className="text-orange-500" />,
    title: 'Real-time sync',
    body: 'Slides appear on audience devices within seconds of advancing.',
  },
  {
    icon: <FontAwesomeIcon icon={faLock} className="text-orange-500" />,
    title: 'Secure & private',
    body: 'Your slides are only visible to people who have your room code.',
  },
  {
    icon: <FontAwesomeIcon icon={faGear} className="text-orange-500" />,
    title: 'Runs in the background',
    body: 'Minimal menubar presence keeps your workflow uninterrupted.',
  },
  {
    icon: <FontAwesomeIcon icon={faStar} className="text-orange-500" />,
    title: 'Free to start',
    body: 'Works with your existing free Sermon Slides account. Upgrade only when you need more.',
  },
]

// ── Inline SVG icons ────────────────────────────────────────────────────────────

function AppleIcon() {
  return (
    <svg viewBox="0 0 814 1000" className="w-5 h-5 fill-current" xmlns="http://www.w3.org/2000/svg">
      <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-37.5-157.2-100c-60.1-71.3-109-181.2-109-285.4 0-167.5 109.3-256.2 216.7-256.2 58.3 0 106.8 38.4 143.4 38.4 35 0 90-40.7 156.6-40.7 25.5 0 108.2 2.6 168.6 81.3zm-201.8-167.2c31.3-37.3 53.1-89.1 53.1-140.9 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 84.2-55.1 135.9 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-70.1z" />
    </svg>
  )
}

function DownloadIcon() {
  return (
    <FontAwesomeIcon icon={faDownload} className="w-5 h-5" />
  )
}
